import classNames from "classnames";
import * as React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { GetQuickJournalLeftPosition } from "source/app/presentation/BottomBar/QuickJournal/View/QuickJournalLayout";
import {
    GetQuickJournalTheme,
    type QuickJournalTheme,
} from "source/app/presentation/BottomBar/QuickJournal/View/QuickJournalTheme";
import type { IQuickJournalState } from "source/app/presentation/BottomBar/QuickJournal/Data/QuickJournalTypes";
import { StyleClassAuto } from "../../Styles/Auto/StyleClassAuto";
import { dynamicOpacityFormClass, sharedFormStyleClass } from "../Form";
import * as formStyles from "../Form.css";
import { dispatchSetFunctionState } from "../../SharedTypes";
import { GetCursorColor } from "./JournalCaptureTextArea/Helpers/JournalCaptureTextAreaHelpers";
import { InputTimeStamp } from "./JournalCaptureTextArea/View/InputTimeStamp";
import { useJournalCaptureTextArea } from "./JournalCaptureTextArea/Hooks/useJournalCaptureTextArea";

interface IJournalCaptureTextAreaInputs {
    id: string;
    onChange: (value: string) => any;
    onClick?: (value: string) => any;
    value: string;
    label?: string;
    onClear?: Function;
    onEnter?: (text: string) => void;
    hideEnterIcon?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    classes?: string;
    readonly?: boolean;
    minWidth?: string;
    maxWidth?: string;
    width?: string;
    placeholder?: string;
    styles?: object;
    dynamicOpacity?: boolean;
    ref?: React.MutableRefObject<any>;
    state: IQuickJournalState;
    setState: dispatchSetFunctionState<IQuickJournalState>;
    onFocusChange?: (hasFocus: boolean) => void;
    autoFocusOnMount?: boolean;
    theme?: QuickJournalTheme;
}

export const JournalCaptureTextArea = (props: IJournalCaptureTextAreaInputs) => {
    const {
        textAreaRef,
        cursorRef,
        timeStamp,
        shouldHideTimeStamp,
        charactersPerSecond,
        isFocused,
        handleClick,
        handleChange,
        handleBlur,
        handleFocus,
        handleBeforeInput,
        handleInput,
        handleKeyDown,
    } = useJournalCaptureTextArea({
        value: props.value,
        state: props.state,
        setState: props.setState,
        autoFocusOnMount: props.autoFocusOnMount,
        onFocusChange: props.onFocusChange,
        onEnter: props.onEnter,
        onChange: props.onChange,
    });
    const quickJournalTheme = GetQuickJournalTheme(props.theme);
    const isOrangeCursor = charactersPerSecond >= 4;
    const isChevronCursor = charactersPerSecond < 2;
    const cursorColor = quickJournalTheme.id === "chromaflare"
        ? quickJournalTheme.input[charactersPerSecond >= 4 ? "cursorHigh" : charactersPerSecond >= 2 ? "cursorMid" : "cursorLow"]
        : GetCursorColor(charactersPerSecond);

    return (
        <div
            style={{
                position: "relative",
                display: "inline-block",
                marginLeft: GetQuickJournalLeftPosition(),
                lineHeight: 0,
                verticalAlign: "bottom",
                opacity: isFocused ? 1 : 0.5,
                transition: "opacity 350ms ease",
            }}
        >
            <InputTimeStamp
                isCyberpunk={quickJournalTheme.id === "cyberpunk"}
                horizontalOffset={props.state.horizontalOffset}
                isHidden={shouldHideTimeStamp}
                isFocused={isFocused}
                timeStamp={timeStamp}
            />
            <textarea
                id="quickJournalTextArea_target"
                rows={1}
                onClick={handleClick}
                ref={textAreaRef}
                value={props.value}
                placeholder={props.placeholder}
                onChange={(event) => handleChange(event.target.value)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onBeforeInput={handleBeforeInput}
                onInput={handleInput}
                className={classNames(
                    StyleClassAuto({
                        fontSize: `16px !important`,
                        lineHeight: `29px !important`,
                        width: `1000px !important`,
                        margin: `0 !important`,
                        paddingLeft: `88px !important`,
                        color: `${quickJournalTheme.input.color} !important`,
                        textShadow: "textShadow" in quickJournalTheme.input
                            ? quickJournalTheme.input.textShadow : "none",
                        caretColor: `transparent`,
                        minHeight: `41px`,
                        whiteSpace: "pre",
                        overflowX: "auto",
                        overflowY: "hidden",
                        resize: `none`,
                        ":focus": {
                            boxShadow: `none !important`,
                        },
                    }),
                    props.classes,
                    [formStyles.formControl, formStyles.noScrollBar],
                    {
                        [formStyles.formControlAnimations]: !IsMobileDevice(),
                    },
                    sharedFormStyleClass,
                    {
                        [dynamicOpacityFormClass]:
                            props.dynamicOpacity && !isFocused,
                    }
                )}
                onKeyDown={handleKeyDown}
            />
            <div
                ref={cursorRef}
                style={{
                    position: "absolute",
                    width: isOrangeCursor ? "16px" : isChevronCursor ? "11px" : "8px",
                    height: "22px",
                    transform: "translateY(-2px)",
                    backgroundColor: cursorColor,
                    borderRadius: isOrangeCursor ? "2px" : isChevronCursor ? "1px" : "0",
                    clipPath: isOrangeCursor
                        ? "polygon(0 0, 100% 0, 57% 50%, 100% 100%, 0 100%, 43% 50%)"
                        : isChevronCursor
                          ? "polygon(0 0, 64% 0, 100% 50%, 64% 100%, 0 100%, 24% 50%)"
                          : "none",
                    boxShadow: isOrangeCursor
                        ? `0 0 8px ${cursorColor}`
                        : "none",
                    filter: isChevronCursor ? "drop-shadow(0 0 3px rgba(0, 0, 0, 0.35))" : "none",
                    pointerEvents: "none",
                    opacity: isFocused ? 0.55 : 0,
                    transition: "opacity 350ms ease",
                    animation: isFocused
                        ? `quickJournalCursorPulse 2s ease-in-out infinite`
                        : "none",
                }}
            />
        </div>
    );
};

const timestampFadeInAnimationName = "quickJournalTimestampFadeIn";
const timestampFadeOutAnimationName = "quickJournalTimestampFadeOut";
const cursorPulseAnimationName = "quickJournalCursorPulse";

if (typeof document !== "undefined") {
    const styleElementId = "quickJournalTimestampFadeAndPlaceholderStyles";

    if (!document.getElementById(styleElementId)) {
        const styleElement = document.createElement("style");
        styleElement.id = styleElementId;
        styleElement.textContent = `
            @keyframes ${timestampFadeInAnimationName} {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes ${timestampFadeOutAnimationName} {
                from { opacity: 1; }
                to { opacity: 0; }
            }
            @keyframes ${cursorPulseAnimationName} {
                0% { opacity: 0.15; }
                50% { opacity: 0.55; }
                100% { opacity: 0.15; }
            }
            #quickJournalTextArea_target::placeholder {
                color: currentColor;
                opacity: 0.42;
            }
        `;
        document.head.appendChild(styleElement);
    }
}
